import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Award, Target, Users, TrendingUp } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: Target,
      title: 'Strategic Focus',
      description: 'We approach every engagement with a clear strategic framework, aligning reimbursement optimization with your organizational goals.',
    },
    {
      icon: Award,
      title: 'Deep Expertise',
      description: 'Our team brings decades of combined experience in Medicare, Medicaid, and commercial payer policy interpretation.',
    },
    {
      icon: Users,
      title: 'Executive Partnership',
      description: 'We work alongside your leadership team, not in isolation. True partnership drives sustainable financial improvement.',
    },
    {
      icon: TrendingUp,
      title: 'Measurable Outcomes',
      description: 'Every recommendation is tied to quantifiable results. We measure success by your bottom-line impact.',
    },
  ];

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className="hero-texture bg-[#0F172A] relative">
        <div className="container-custom relative z-10 py-24 sm:py-32">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#0D9488] mb-4">
              About Us
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-white font-['Outfit']">
              Simplifying Complex Reimbursement Challenges
            </h1>
            <p className="mt-6 text-xl text-gray-300 leading-relaxed">
              For over two decades, we've helped hospitals and health systems navigate the complexities of healthcare reimbursement with strategic clarity.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="section-padding bg-white" data-testid="company-overview">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="divider mb-6"></div>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-[#0F172A] font-['Outfit'] mb-6">
                Built on Deep Healthcare Expertise
              </h2>
              <div className="space-y-6 text-[#64748B] leading-relaxed">
                <p>
                  Symplicity Revenue Solutions was founded on a simple premise: healthcare reimbursement shouldn't be a mystery. Our team of experts has spent decades inside the healthcare revenue cycle—working with hospitals, health systems, and physician groups to decode payer behavior and optimize financial performance.
                </p>
                <p>
                  We've witnessed firsthand how inconsistent documentation, misaligned coding practices, and reactive payer strategies leave millions of dollars on the table. More importantly, we've seen how strategic, proactive reimbursement management transforms financial outcomes.
                </p>
                <p>
                  Our approach combines rigorous payer policy analysis with practical, implementable solutions. We don't just identify problems—we build the frameworks and processes that drive lasting improvement.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] bg-[#0F172A] rounded-lg overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_revenue-integrity/artifacts/nhdua7c7_Collaborating%20in%20a%20modern%20healthcare%20environment.png"
                  alt="Healthcare team collaboration"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#0D9488] text-white p-6 rounded-lg">
                <div className="text-4xl font-light font-['Outfit']">30+</div>
                <div className="text-sm uppercase tracking-wider">Years Experience</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-padding bg-[#FAFAFA]" data-testid="values-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#0D9488] mb-4">
              Our Approach
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-[#0F172A] font-['Outfit']">
              What Sets Us Apart
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white border border-[#E2E8F0] p-8 card-hover"
                data-testid={`value-card-${index}`}
              >
                <div className="icon-container mb-6">
                  <value.icon className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] font-['Outfit'] mb-3">
                  {value.title}
                </h3>
                <p className="text-[#64748B] leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="section-padding bg-white" data-testid="leadership-section">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-sm font-semibold uppercase tracking-wider text-[#0D9488] mb-4">
              Leadership
            </p>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-[#0F172A] font-['Outfit']">
              Executive Team
            </h2>
          </div>

          <div className="space-y-12">
            {/* Deirdre Lewis */}
            <div className="bg-[#FAFAFA] border border-[#E2E8F0] p-8 sm:p-12" data-testid="founder-bio-deirdre">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="w-48 h-48 flex-shrink-0 rounded-lg overflow-hidden">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_revenue-integrity/artifacts/k6nee7eu_Confident%20professionalism%20in%20a%20modern%20office.png"
                    alt="Deirdre D. Lewis, Founder & Principal Consultant"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-[#0F172A] font-['Outfit'] mb-1">
                    Deirdre D. Lewis, MS, RHIA, CCS, CDIP
                  </h3>
                  <p className="text-[#0D9488] font-medium mb-6">Founder & Principal Consultant</p>
                  <div className="space-y-4 text-[#64748B] leading-relaxed">
                    <p>
                      Deirdre is a healthcare reimbursement and revenue integrity expert with over 30 years of experience in Health Information Management (HIM), Coding, and Clinical Documentation Integrity (CDI). She partners with hospitals and health systems to increase revenue, improve reimbursement accuracy, and ensure compliance by identifying and capturing missed revenue opportunities.
                    </p>
                    <p>
                      She brings expertise across Medicare, Medicaid, and commercial payers, translating complex regulations into actionable, compliant strategies that deliver measurable financial results. Her work focuses on data-driven insights, targeted DRG validation, and integrated coding and CDI initiatives to ensure documentation accurately reflects patient acuity and supports optimal reimbursement.
                    </p>
                    <p>
                      Deirdre has worked with large health systems, academic medical centers, Level 1 trauma centers, and children's hospitals, driving sustainable improvements in Case Mix Index (CMI), denial reduction, and overall revenue integrity.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sharon Bond */}
            <div className="bg-[#FAFAFA] border border-[#E2E8F0] p-8 sm:p-12" data-testid="founder-bio-sharon">
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="w-48 h-48 flex-shrink-0 rounded-lg overflow-hidden bg-[#0F172A]">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_revenue-integrity/artifacts/1o66g8dl_image.png"
                    alt="Sharon Bond, Co-Founder & Principal Consultant"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <h3 className="text-2xl font-semibold text-[#0F172A] font-['Outfit'] mb-1">
                    Sharon Bond, RN, MHA, CCS, CPC, CDIP
                  </h3>
                  <p className="text-[#0D9488] font-medium mb-6">Co-Founder & Principal Consultant</p>
                  <div className="space-y-4 text-[#64748B] leading-relaxed">
                    <p>
                      Sharon is an accomplished healthcare professional with over 30 years of nursing experience and more than two decades as a health information management consultant. She brings deep expertise in clinical documentation improvement (CDI), inpatient coding, and regulatory compliance, with a strong focus on helping healthcare organizations enhance accuracy, quality, and financial performance.
                    </p>
                    <p>
                      Sharon is known for her ability to translate complex clinical and coding concepts into practical strategies that drive measurable results. She is passionate about advancing documentation integrity, improving case mix accuracy, and supporting healthcare teams through education and collaboration.
                    </p>
                    <p>
                      Throughout most of her career, Sharon has partnered with hospitals and providers nationwide to implement and optimize CDI programs, conduct coding audits, and deliver targeted education for clinicians, coders, and CDI teams. From the payor side, she formerly worked as a fraud, waste, and abuse investigator as well as conducted OIG audits as a contractor, giving her a well-rounded perspective on compliance and risk mitigation.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-[#0F172A] py-20" data-testid="mission-section">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <div className="quote-mark">"</div>
            <blockquote className="text-2xl sm:text-3xl text-white font-['Outfit'] font-light leading-relaxed mt-4">
              To simplify complex reimbursement challenges and drive measurable financial and compliance outcomes for healthcare organizations.
            </blockquote>
            <p className="mt-8 text-[#0D9488] font-semibold uppercase tracking-wider">Our Mission</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#FAFAFA]">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl tracking-tight font-semibold text-[#0F172A] font-['Outfit'] mb-6">
            Ready to Partner with Us?
          </h2>
          <p className="text-lg text-[#64748B] max-w-2xl mx-auto mb-8">
            Let's discuss how our expertise can help your organization achieve its financial and compliance objectives.
          </p>
          <Link to="/contact" data-testid="about-cta">
            <Button className="bg-[#0D9488] hover:bg-[#0f766e] text-white rounded-md px-10 py-6 text-lg group">
              Schedule a Consultation
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
